/**
 * The built-in sheet: what a list prints when its callsite supplies no
 * renderer of its own.
 *
 * Columns come from `tableToRows` — the SAME projection the CSV export uses.
 * Sharing it is the point: a printout whose columns disagree with the
 * spreadsheet is a bug nobody catches until the paper is in a hand, and one
 * projection makes that unreachable rather than merely unlikely.
 *
 * Styling is SELF-CONTAINED, and that is a constraint rather than a
 * preference: this subtree is rendered into a separate window whose document
 * links none of the app's CSS (#1902). Tailwind classes and `var(--border)`
 * resolve to nothing there, so the sheet ships the rules it needs as a
 * `<style>` of its own.
 */
import type { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { tableToRows } from '../../list/table-export';
import type { ExportContext } from '../../registry/export-text';

export interface PrintSheetProps<T, TValue = unknown> {
  columns: ColumnDef<T, TValue>[];
  rows: readonly T[];
  visibility: VisibilityState;
  /**
   * The `{ t, format }` seam slice `tableToRows` needs, passed as a PROP rather
   * than read from `useResourceKit()` here: this subtree renders into a
   * separate window, and keeping the sheet a pure function of its inputs is
   * what lets the CSV and the paper be tested against one projection.
   */
  ctx: ExportContext;
  /** Shown above the table so two printouts are tellable apart. */
  title: string;
}

/** Plain, absolute-unit CSS — no tokens, no utility classes, no cascade. */
const SHEET_CSS = `
.print-sheet { padding: 0 4mm; font-size: 10pt; line-height: 1.35; }
.print-sheet-title { margin: 0 0 3mm; font-size: 14pt; font-weight: 600; }
.print-sheet table { width: 100%; border-collapse: collapse; }
.print-sheet th, .print-sheet td {
  padding: 1mm 2mm;
  border: 0.2mm solid #777;
  text-align: left;
  vertical-align: top;
}
.print-sheet th { font-weight: 600; }
.print-sheet tbody tr { break-inside: avoid; }
`;

export function PrintSheet<T, TValue = unknown>({
  columns,
  rows,
  visibility,
  ctx,
  title,
}: PrintSheetProps<T, TValue>) {
  const [header, ...body] = tableToRows(columns, rows, visibility, ctx);

  return (
    <div className="print-sheet">
      <style>{SHEET_CSS}</style>
      <h1 className="print-sheet-title">{title}</h1>
      <table>
        <thead>
          <tr>
            {(header ?? []).map((label, i) => (
              <th key={i}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((cells, r) => (
            // Rows carry no stable id at this layer — the projection
            // is already flattened to text — so the index is the
            // honest key for a list that never reorders.
            <tr key={r}>
              {cells.map((cell, c) => (
                <td key={c}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
